import { get } from 'svelte/store';
import { format } from 'svelte-i18n';

import type { ChartStatisticRangePeriod } from 'src/types/Chart';
import type { SelectraPricePeriod, SelectraPricesResponse } from 'src/types/Selectra';
import { user_settings } from 'src/stores';
import { PRICE_PER_KWH_DECIMALS } from 'src/helpers/units_helpers';

const SELECTRA_PERIOD_COLORS = ['color-primary', 'color-cyan', 'color-orange', 'color-purple'];

const startOfLocalDay = (date: Date): number =>
	new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export const isActiveSelectraPeriod = (period: SelectraPricePeriod): boolean => {
	const now = Date.now();
	return new Date(period.start).getTime() <= now && now < new Date(period.end).getTime();
};

export const getActiveSelectraPeriod = (
	prices: SelectraPricesResponse | null,
): SelectraPricePeriod | null => {
	if (!prices?.prices?.length) return null;

	return prices.prices.find(isActiveSelectraPeriod) ?? prices.prices[0];
};

export const formatSelectraPeriodDate = (iso: string): string => {
	try {
		return new Date(iso).toLocaleString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	} catch {
		return iso;
	}
};

export const formatSelectraPeriodRange = (start: string, end: string): string =>
	`${formatSelectraPeriodDate(start)} — ${formatSelectraPeriodDate(end)}`;

export const findSelectraPeriodAt = (
	periods: SelectraPricePeriod[],
	slotStart: number,
	slotEnd: number = slotStart + 30 * 60 * 1000,
): SelectraPricePeriod | null => {
	for (const period of periods) {
		const start = new Date(period.start).getTime();
		const end = new Date(period.end).getTime();
		if (slotStart < end && slotEnd > start) return period;
	}

	return null;
};

export const getSelectraChartDays = (periods: SelectraPricePeriod[]): number[] => {
	const daySet = new Set<number>();

	for (const period of periods) {
		const startDay = startOfLocalDay(new Date(period.start));
		const endDay = startOfLocalDay(new Date(period.end));
		let day = startDay;

		while (day <= endDay) {
			daySet.add(day);
			day += 24 * 60 * 60 * 1000;
		}
	}

	return [...daySet].sort((a, b) => a - b);
};

export const formatSelectraPrice = (value: number): string => {
	const price = Number.isFinite(value) ? value : 0;
	return price.toFixed(PRICE_PER_KWH_DECIMALS);
};

export const hasMultipleSelectraEntriesOnSameDay = (periods: SelectraPricePeriod[]): boolean => {
	const counts = new Map<number, number>();

	for (const period of periods) {
		const day = startOfLocalDay(new Date(period.start));
		const next = (counts.get(day) ?? 0) + 1;
		if (next > 1) return true;
		counts.set(day, next);
	}

	return false;
};

export const getSelectraChartRangePeriod = (
	range: number,
	days: number[],
	periods: SelectraPricePeriod[],
): ChartStatisticRangePeriod => {
	const isWeekRange = range === -1 && days.length > 1;
	if (!isWeekRange) return 'day';

	return hasMultipleSelectraEntriesOnSameDay(periods) ? 'day' : 'week';
};

export const getSelectraRangeOptions = (periods: SelectraPricePeriod[]) => {
	const $format = get(format);
	const days = getSelectraChartDays(periods);
	const options: { title: string; value: number }[] = [];

	if (days.length > 1) {
		options.push({
			title: $format('option_week', { default: 'Week' }),
			value: -1,
		});
	}

	for (const dayStart of days) {
		const date = new Date(dayStart);
		options.push({
			title: date.toLocaleDateString(undefined, {
				weekday: 'long',
				month: 'short',
				day: 'numeric',
			}),
			value: dayStart,
		});
	}

	return options;
};

export const getSelectraPeriodBgColor = (
	period: SelectraPricePeriod,
	periods: SelectraPricePeriod[],
): string => {
	if (isActiveSelectraPeriod(period)) return 'color-green';

	const idx = periods.indexOf(period);
	return SELECTRA_PERIOD_COLORS[idx % SELECTRA_PERIOD_COLORS.length] ?? 'color-primary';
};

export const syncLiveTariffFromSelectra = (prices?: SelectraPricesResponse | null): boolean => {
	const activePeriod = getActiveSelectraPeriod(prices ?? null);
	if (!activePeriod) return false;

	const settings = get(user_settings);
	const existing = settings?.power_prices_live_tariff ?? {};

	user_settings.set({
		...settings,
		power_prices_live_tariff: {
			...existing,
			name: activePeriod.name,
			price: activePeriod.price,
		},
	});

	return true;
};

export const clearLiveTariffFromUserSettings = (): void => {
	const settings = { ...get(user_settings) };
	delete settings.power_prices_live_tariff;
	user_settings.set(settings);
};
